import { useEffect } from "react";
import type { User } from "@/features/auth/services/firebase";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { debugLog } from "./authSessionHelpers";

interface UseProfileRefreshParams {
  user: User | null;
  accountDisabled: boolean;
  fetchProfile: (firebaseUser: User) => Promise<UserProfile | null>;
  setProfile: (profile: UserProfile | null) => void;
  authGenerationRef: React.MutableRefObject<number>;
}

export function useProfileRefresh({
  user,
  accountDisabled,
  fetchProfile,
  setProfile,
  authGenerationRef,
}: UseProfileRefreshParams): void {
  useEffect(() => {
    if (!user || !accountDisabled) return;

    const MIN_REFRESH_INTERVAL = 30000;
    let lastRefresh = 0;

    const refreshProfileOnVisible = async () => {
      const currentGen = authGenerationRef.current;
      debugLog("Tab visible, refreshing profile for disabled account", { gen: currentGen });
      const userProfile = await fetchProfile(user);
      if (authGenerationRef.current !== currentGen) return;
      setProfile(userProfile);
    };

    const handleVisibilityChange = () => {
      const now = Date.now();
      if (
        document.visibilityState === "visible" &&
        now - lastRefresh > MIN_REFRESH_INTERVAL
      ) {
        lastRefresh = now;
        void refreshProfileOnVisible();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [accountDisabled, fetchProfile, setProfile, user, authGenerationRef]);
}
